import { useMutation } from '@tanstack/react-query'
import { Button, EmptyState, Modal, Text, UnsavedChangesDialog, useUnsavedChangesWarning } from '@viapeople/components'
import MarkdownEditor, { MarkdownPreview } from '@viapeople/components/src/components/MarkdownEditor'
import { useEffect, useState } from 'react'
import { useToast } from '../../../../utils/ToastContext'
import { queryClient, trpc } from '../../../../utils/trpc'
import type { Cycle } from '../../types'
import { useCycleFromParams } from '../../useCycleFromParams'

const FooterModal = ({
	isOpen,
	onClose,
	isEditing,
	cycle,
}: {
	isOpen: boolean
	onClose: () => void
	isEditing: boolean
	cycle?: Cycle
}) => {
	const toast = useToast()
	const { mutate: editCycle, isPending } = useMutation(
		trpc.admin.cycles.edit.mutationOptions({
			onSuccess: () => {
				queryClient.setQueryData(
					trpc.admin.cycles.getById.queryKey({
						id: cycle?.id,
					}),
					() => ({
						...(cycle as Cycle),
						emailFooter: _value || null,
					}),
				)
				toast.showToast('Footer updated successfully', 'info')
				onClose()
			},
			onError: () => {
				toast.showToast('Error updating footer', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.getById.queryKey({ id: cycle?.id }),
					exact: false,
					refetchType: 'all',
				})
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.search.queryKey(),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	)

	const [_value, setValue] = useState(cycle?.emailFooter || '')
	const isDirty = _value !== (cycle?.emailFooter || '')
	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty && isOpen)

	useEffect(() => {
		setValue(cycle?.emailFooter || '')
	}, [cycle?.emailFooter])

	const onSave = (footer: string) => {
		if (cycle?.id) {
			editCycle({
				id: cycle.id,
				organizationId: cycle.organizationId,
				emailFooter: footer,
				metadata: null,
				triggerLogId: null,
			})
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={`${isEditing ? 'Edit' : 'Add'} Global Footer`}
			className='min-w-[800px]'>
			<MarkdownEditor
				defaultValue={_value}
				onChangeMarkdown={(e) => setValue(e)}
				placeholder='Enter the global footer HTML here (you can use variables too)'
				rows={10}
			/>

			<div className='flex flex-row justify-between mt-6'>
				{isEditing ? (
					<Button
						variant='destructive'
						isLoading={isPending}
						disabled={isPending}
						onClick={() => {
							setValue('')
							onSave('')
						}}>
						Delete
					</Button>
				) : (
					<div />
				)}
				<div className='flex flex-row gap-4 mt-auto self-end'>
					<Button
						variant='secondary'
						isLoading={isPending}
						disabled={isPending}
						onClick={() => {
							setValue(cycle?.emailFooter || '')
							onClose()
						}}>
						Cancel
					</Button>
					<Button onClick={() => onSave(_value || '')} isLoading={isPending} disabled={isPending}>
						Save
					</Button>
				</div>
			</div>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</Modal>
	)
}

export function CycleEmailsFooterTab() {
	const cycle = useCycleFromParams()
	const [openModal, setOpenModal] = useState(false)

	return (
		<div className='p-6'>
			<Text variant='h6' weight='semibold' className='text-left'>
				Global Email Footer
			</Text>

			{!cycle?.emailFooter && (
				<EmptyState
					title='Global Email Footer'
					description='Add a global footer to all emails sent out for this cycle'
					actionLabel='Add Global Footer'
					onAction={() => {
						setOpenModal(!!cycle?.id)
					}}
				/>
			)}

			{cycle?.emailFooter && (
				<div className='mt-4'>
					<MarkdownPreview markdown={cycle.emailFooter} />

					<Button variant='primary' onClick={() => setOpenModal(true)} className='ml-auto'>
						Edit Global Footer
					</Button>
				</div>
			)}

			<FooterModal
				isOpen={openModal}
				onClose={() => setOpenModal(false)}
				isEditing={!!cycle?.emailFooter}
				cycle={cycle}
			/>
		</div>
	)
}
